// Fire-and-forget Telegram alert from the frontend.
// Wraps the client-alert edge function. Never throws.

import { supabase } from "@/integrations/supabase/client";

type ClientAlertArgs = {
  context: string;
  message?: string;
  meta?: Record<string, unknown>;
};

// One id per browser session, attached to every alert and forwarded to the
// backend so a single user complaint pulls up one connected timeline instead of
// scattered, unrelated events.
export const SESSION_TRACE_ID: string =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `t-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Browser-side network blips (offline, flaky wifi, preview fetch proxy,
// ad-block extensions, tab backgrounded during navigation) surface as these
// error strings. They are not app bugs and produce huge Telegram noise, so
// swallow them at the alert boundary.
const NETWORK_NOISE = [
  "failed to fetch",
  "networkerror",
  "load failed",
  "network request failed",
  "the internet connection appears to be offline",
  "err_internet_disconnected",
  "err_network_changed",
  "err_name_not_resolved",
  "err_connection_",
];

// Expired/stale access tokens are handled by the Supabase client's automatic
// refresh (typically after a tab wakes from sleep). They self-heal on the next
// request and are not app bugs, so keep them out of Telegram.
const AUTH_NOISE = [
  "jwt expired",
  "jwt is expired",
  "token is expired",
  "invalid jwt",
  "invalid claim: missing sub claim",
  "refresh token not found",
  "refresh_token_not_found",
  "session missing",
  "auth session missing",
];

function isTransientNetworkError(message: string | undefined): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return (
    NETWORK_NOISE.some((needle) => m.includes(needle)) ||
    AUTH_NOISE.some((needle) => m.includes(needle))
  );
}

// Filtered errors used to be thrown away entirely, which meant a real outage
// looked exactly like one person's flaky wifi. Now they are counted, and a
// sustained burst still escalates — quiet for noise, loud for an outage.
const SPIKE_WINDOW_MS = 2 * 60 * 1000;
const SPIKE_THRESHOLD = 8;
let suppressedTimestamps: number[] = [];
let lastSpikeAlertAt = 0;

function recordSuppressed(context: string, message: string): void {
  const now = Date.now();
  suppressedTimestamps = suppressedTimestamps.filter((t) => now - t < SPIKE_WINDOW_MS);
  suppressedTimestamps.push(now);
  if (
    suppressedTimestamps.length >= SPIKE_THRESHOLD &&
    now - lastSpikeAlertAt > SPIKE_WINDOW_MS
  ) {
    lastSpikeAlertAt = now;
    const count = suppressedTimestamps.length;
    suppressedTimestamps = [];
    sendAlert({
      context: "📉 Connectivity error spike (normally filtered as noise)",
      message: `${count} network/auth errors in 2 minutes — last: ${message}`,
      meta: { lastContext: context, suppressedCount: count },
    });
  }
}

export function reportClientError(args: ClientAlertArgs): void {
  if (isTransientNetworkError(args.message)) {
    recordSuppressed(args.context, args.message ?? "");
    return;
  }
  sendAlert(args);
}

function sendAlert(args: ClientAlertArgs): void {
  // Fire and forget. Never await, never throw.
  void (async () => {
    try {
      await supabase.functions.invoke("client-alert", {
        headers: { "x-trace-id": SESSION_TRACE_ID },
        body: {
          context: args.context,
          message: args.message ?? "",
          path: typeof window !== "undefined" ? window.location.pathname : undefined,
          meta: { traceId: SESSION_TRACE_ID, ...(args.meta ?? {}) },
        },
      });
    } catch {
      // swallow — alert path must not disrupt UX
    }
  })();
}

// Convenience for .catch handlers
export const catchToAlert = (context: string, meta?: Record<string, unknown>) =>
  (err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    reportClientError({ context, message, meta });
  };